/*
 * Common site Uitlity module
*/
import modulelist from './module-list';
import { sha256 } from 'js-sha256';
import Cookie from 'js-cookie';
import { fmkeys } from '../fmpost-key-mappings';
import { GA4Utilities } from './index';
import RestrictedDomains from '../restricted-domains';
import { cfMarketingLookupTable } from './cf-marketing-lookup-table';
import { preserveInConsole, handleClientError } from '../client-side-error-handler';

// A generic function to make API calls
const makeApiCall = ({ url, method, headers, body }) => {
  if ('fetch' in window) {
    return fetch(url, {
      body,
      method,
      headers
    }).then(res => res.json());
  }
  return jQuery.ajax({
    url,
    method,
    headers,
    data: body
  });
};

// Load a local file using webpack code-splitting: return a promise with single or multiple values
// arguments: String 'ModuleName' or Array of Strings ['ModuleName']
const onModuleLoad = function (module) {
  function getPromise (moduleName) {
    if (typeof modulelist[moduleName] === 'function') {
      return modulelist[moduleName]().then(val => {
        // returning default object if exists
        if (val.__esModule) {
          if (typeof val.default === 'object') {
            val.default.__esModuleOriginal = val;
          }
          return val.default;
        } else {
          return val;
        }
      });
    } else {
      log(
        'Requested module is not defined', 'warning',
        `Module - ${moduleName}`, 0, ''
      );
      return Promise.reject(new Error('Module not defined'));
    }
  }

  if (typeof module === 'string') {
    return getPromise(module);
  } else if (module instanceof Array) {
    return Promise.all(module.map(getPromise));
  }
};

const loadScript = (src, callback) => {
  return $.getScript(src, callback).fail(xhr => {
    /* Since, it is out of scope for us to handle maxmind failures,
     * we are logging it as `warning` so that it will not be recorded
     * in our failure reports of Sentry */
    const logLevel = [
      'geoip-js.com'
    ].some(elem => src.includes(elem)) ? 'warning' : 'error';
    log(
      `Requested script failed to load with ${xhr.status}`,
      logLevel, `Script src - ${src}`, 2, xhr
    );
    callback(new Error(`Requested script failed to load with ${xhr.status} error`));
  });
};

const loadMaxmindScript = (src, callback) => {
  return jQuery.ajax({
    url: src,
    dataType: 'script',
    error: function (err) {
      log(
        `Requested script failed to load with ${err.status}`,
        'warning', `Script src - ${src}`, 2, err
      );
      callback(new Error(`Requested script failed to load with ${err.status} error`));
    },
    success: function () {
      callback();
    },
    timeout: 100000 // Increase timeout to 100 seconds
  });
};

/*
 * Get a specific query parameter from the URL
 */
const getParameterByName = (name) => {
  name = name.replace(/[[]]/, '\\[').replace(/[\]]/, '\\]');
  var regexS = '[\\?&]' + name + '=([^&#]*)';
  var regex = new RegExp(regexS);
  var results = regex.exec(window.location.search);
  if (results == null) {
    return '';
  }
  return decodeURIComponent(results[1].replace(/\+/g, ' '));
};

/*
  Get the sha256 hash for the given input
*/

const getShaHash = (string) => {
  try {
    return sha256(string);
  } catch (e) {
    log(
      'SiteUtilites - Error: Computing Sha256 hash',
      'error',
      'FW.SiteUtilites.getShaHash',
      2,
      e
    );
    return '';
  }
};

/*
 * Get the useragent of the navigator
 */
const getUserAgent = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/Firefox/.test(userAgent)) {
    return 'firefox';
  }
  if (/Chrome/.test(userAgent)) {
    return 'Chrome';
  }
  return '';
};

const getUserAgentForMobile = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }
  if (/android/i.test(userAgent)) {
    return 'Android';
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iOS';
  }
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Playbook|tablet|silk|(android(?!.*mobile))/i.test(userAgent)) {
    return 'Mobile';
  }
  return '';
};
/*
 * Redirect to URL (Included IE Support)
 */
const redirectToURL = (url) => {
  const ua = navigator.userAgent.toLowerCase();
  const isIE = ua.indexOf('msie') !== -1;
  const version = parseInt(ua.substr(4, 2), 10);

  // Internet Explorer 8 and lower
  if (isIE && version < 9) {
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
  } else {
    // All other browsers can use the standard window.location.href (they don't lose HTTP_REFERER like IE8 & lower does)
    window.location.href = url;
  }
};

/*
 * Convert the time from AM/PM  to 24 hour format and viceversa
 * 'time' should be Eg: 8.30 PM || 1.30 AM || 12.00 PM || 12.00 AM to convert to 24 hours
 * 'time' should be 4.40, 14.20 to convert to AM/PM
 * 'format' can be "AMPM" / "24"
 */
const convertTimeHours = (timeStr, format) => {
  timeStr = timeStr.toString();
  if (format === '24') {
    if (timeStr.indexOf('AM') !== -1 || timeStr.indexOf('PM') !== -1) {
      let [time, modifier] = timeStr.split(' ');
      let [hours, minutes] = time.split(':');
      hours = Number(hours);
      minutes = Number(minutes);
      modifier = modifier.toUpperCase();
      if (hours <= 12) {
        hours = (hours === 12) ? 0 : hours;
        hours = (modifier === 'PM') ? (hours + 12) : hours;
        hours = (hours < 10) ? ('0' + hours.toString()) : hours.toString();
        minutes = (minutes < 10) ? ('0' + minutes.toString()) : minutes.toString();
        return hours + ':' + minutes;
      } else {
        throw new Error('Invalid format of Time in the format "AMPM"');
      }
    } else {
      throw new Error('Time is not in AM/PM format, but the format is chosen as "AMPM"');
    }
  } else if (format === 'AMPM') {
    if (timeStr.indexOf('AM') === -1 && timeStr.indexOf('PM') === -1) {
      let [hours, minutes] = timeStr.split('.');
      hours = Number(hours);
      minutes = Number(minutes);
      minutes = (minutes < 10) ? ('0' + minutes.toString()) : minutes.toString();
      const modifier = (hours < 12) ? 'AM' : 'PM';
      hours = (hours < 12) ? hours : (hours - 12);
      hours = (hours < 10) ? ('0' + hours.toString()) : (hours).toString();
      return hours + '.' + minutes + ' ' + modifier;
    } else {
      throw new Error('Time is not in 24hours format');
    }
  } else {
    throw new Error('Unknown format of time');
  }
};

/*
 * message - string - informative text message
 * context - string - text indicating context of log (eg.
 *                 "Signup", "Location Utilities")
 * severity - need to remove this parameter since the log
 *     severity is handled in `level` type
 * dump - mixed - optional data to log with message
 */
const log = (message = '', level = '', context = '', severity = 0, dump = {}) => {
  const levels = [
    'debug',
    'info',
    'warning',
    'error',
    'fatal'
  ];

  try {
    if (typeof message !== 'string') {
      message = JSON.stringify(message);
    }
    if (typeof level !== 'string') {
      message = 'Type (Level param) of bug not defined properly';
      level = 'warning';
    }
    if (typeof context !== 'string') {
      context = '';
    }

    if (levels.indexOf(level) < 2) {
      return;
    }

    handleClientError({
      stringifiedError: dump instanceof Error ? String(dump) : message,
      message: message,
      name: level,
      stack: (dump instanceof Error && dump.stack) || undefined,
      lineNumber: (dump instanceof Error && dump.lineNumber) || undefined,
      columnNumber: (dump instanceof Error && dump.columnNumber) || undefined,
      fileName: (dump instanceof Error && dump.fileName) || context,
      source: 'LogFn'
    });
  } catch (e) {
    preserveInConsole(e);
  }
};

/*
 * Post data to the Freshmarketer Lambda micro-service
 * Object Structure
 * {
 *   freshmarketerObject:{
 *   }
 * }
 */

const postToKinesis = (kinesisPayload = null, isRequestForSignup = false) => {
  const { growSumoPost, fmCollectorPost, awinConversionPost, admitadPost } = kinesisPayload || {};
  const { partner_key: partnerKey } = growSumoPost || {};

  if (!awinConversionPost) {
    delete kinesisPayload.awinConversionPost;
  }

  // Deleting Admitad data without uid
  if (!admitadPost) {
    delete kinesisPayload.admitadPost;
  }
  // Request for GrowSumo Conversion API without partner_key
  if (growSumoPost && !partnerKey) {
    if (!fmCollectorPost && !admitadPost) {
      return jQuery.Deferred().resolve();
    } else {
      delete kinesisPayload.growSumoPost;
    }
  }
  const url = 'https://alfred.freshworks.com/v1/kinesis-post';
  const payload = JSON.stringify({
    StreamName: 'blocking-api-calls',
    PartitionKey: 'kinesisPartitionKey',
    Data: kinesisPayload || {
      fmCollectorPost: JSON.parse(localStorage.getItem('kinesisFmCollectorPost')),
      // growSumoPost: JSON.parse(localStorage.getItem('kinesisGrowSumoPost')),
      performance: JSON.parse(localStorage.getItem('kinesisPerformance'))
    }
  });

  const formSubmitEvent = new CustomEvent('formSubmitEvent', {
    detail: {
      payload,
      system: 'kinesisVulcan'
    }
  });
  window.dispatchEvent(formSubmitEvent);

  if (canIUseSendBeacon() && isRequestForSignup) {
    navigator.sendBeacon(url, payload);
  } else {
    return jQuery.ajax({
      data: payload,
      method: 'POST',
      url,
      contentType: 'application/json'
    });
  }
};

const buildGrowSumoPayload = (growsumokey, productDomain) => {
  const gsdata = JSON.parse(localStorage.getItem('gsdata')) || {};
  const xid = Cookie.get('gsxid') || '';
  const partnerKey = Cookie.get('growSumoPartnerKey') || '';
  const payload = {
    identifier: 'All partners - growsumo post payload',
    xid,
    email: gsdata.email_id,
    name: gsdata.first_name,
    origin: window.location.hostname,
    public_key: growsumokey,
    partner_key: partnerKey
  };
  payload.customer_key = gsdata.domain_link || (gsdata.domain_name && (gsdata.domain_name + productDomain)) || gsdata.account_id || '';

  return payload;
};

const buildFreshmarketerObject = (obj, triggerCompleteEvent, form, postToKinesis = true) => {
  const $form = $(form);
  const fmEventCustomInputs = {};
  const $freshmarketerCustomInputs = $form.find('.custom-inputs.type-freshmarketer');
  let payload;
  if ($freshmarketerCustomInputs.length > 0) {
    customHiddenFormFields($freshmarketerCustomInputs, obj);
  }
  const product = $('body').attr('data-product-name');
  let fmAssetId = $form.find('.fm-asset-id').val() || $form.data('fm_asset_id') || 3649313;
  const externalDeferrer = $.Deferred();
  const postSuccess = () => {
    if (postToKinesis) externalDeferrer.resolve();
    jQuery('body').trigger('freshmarketerPostCompleted');
  };
  try {
    // Add custom field object if it doesn't exist
    if (!obj.freshmarketerObject.custom_field) {
      obj.freshmarketerObject.custom_field = {};
    }

    // Adding Lists Array only if there was a valid list ID.
    const listId = obj.freshmarketerObject.custom_field && obj.freshmarketerObject.custom_field.cf_freshmarketer_list;
    if (listId) {
      obj.freshmarketerObject.custom_field['cf_update_list_id'] = listId;
      obj.freshmarketerObject.list_ids = [listId];
      obj.freshmarketerObject.append_values = ['list_ids'];
    }
    // Send the product for every lead. Check for overrides first and if product is mentioned in the override send that instead of taking it from the default product data attribute.
    if (obj.freshmarketerObject.overrides && obj.freshmarketerObject.overrides.product) {
      const productOverride = obj.freshmarketerObject.overrides.product;
      obj.freshmarketerObject.custom_field[`cf_${productOverride}`] = 'true';
    } else {
      if (obj.freshmarketerObject.custom_field) {
        obj.freshmarketerObject.custom_field[`cf_${product}`] = 'true';
      }
    }
    // Send email hash
    if (obj.freshmarketerObject.email) {
      window.FM && window.FM.associateVisitor && window.FM.associateVisitor(obj.freshmarketerObject.email);
      const emailHash = getShaHash(obj.freshmarketerObject.email);
      obj.freshmarketerObject.custom_field['cf_ehash'] = emailHash;
    }

    /// Send Field only for Germany, Austria and Switzerland
    const gdprDoubleOptInCountries = ['Germany', 'Austria', 'Switzerland'];
    if (obj.freshmarketerObject.country && gdprDoubleOptInCountries.includes(obj.freshmarketerObject.country)) {
      obj.freshmarketerObject.custom_field['cf_gdprdoubleopt-in'] = obj.freshmarketerObject.contactable;
    }
    obj.freshmarketerObject.custom_field['cf_Fworksgenericsignupreferrer'] = Cookie.get('fw_sflu') || window.location.href || '';
    // Remove the overrides object that the backend doesn't expect.
    obj.freshmarketerObject.overrides && delete obj.freshmarketerObject.overrides;
    passPathFactoryParamsToFmarketer(obj);
    passMarketplaceIntegrationToFmarketer(obj);
    passTagsToFmarketer(obj);
    filterFmCustomfields(obj, fmEventCustomInputs, $form);
    const fmobj = restructureFmObj(obj, $form);

    if (fmAssetId) {
      const pathfactoryAssetIdArr = getParameterByName('pf_content_download_asset_id') || getParameterByName('pf_contact_asset_id');
      if (pathfactoryAssetIdArr) {
        const parsedCf = pathfactoryAssetIdArr.split(';');
        if (parsedCf.length) {
          fmAssetId = parsedCf[0];
        }
      }
      const product = (fmobj.contact && fmobj.contact.product) || null;
      const freshmarketerEventData = {
        email: fmobj.unique_identifier && fmobj.unique_identifier.emails,
        event_data: {
          email_id: fmobj.unique_identifier && fmobj.unique_identifier.emails,
          allocadia_asset_id: fmAssetId,
          domainUrl: (fmobj.contact && fmobj.contact.domainUrl) || null,
          product,
          accountId: (fmobj.contact && fmobj.contact.accountId) || null,
          composite_stitching_key: (fmobj.contact && fmobj.contact.composite_stitching_key) || null
        }
      };
      if (listId) {
        freshmarketerEventData['event_data']['list_id'] = listId;
      }
      if ($form.data('fm_asset_id') && product && product === 'FW CRM') {
        freshmarketerEventData['event_data']['allocadia_asset_id'] = $form.data('fm_asset_id');
      }
      for (const cf in fmEventCustomInputs) {
        freshmarketerEventData['event_data'][cf] = fmEventCustomInputs[cf];
      }
      const zargetFallbackUserId = +new Date() + 'r' + Math.random();
      freshmarketerEventData['event_data']['zarget_user_id'] = Cookie.get('zarget_user_id') || zargetFallbackUserId;
      freshmarketerEventData['event_data']['allocadia_page_url'] = window.location.href;
      const tacticId = getTacticID();
      const email = (freshmarketerEventData.email) ? freshmarketerEventData.email : null;
      const assetId = (freshmarketerEventData.event_data && freshmarketerEventData.event_data.allocadia_asset_id) ? freshmarketerEventData.event_data.allocadia_asset_id : null;
      const compositeKey = getCompositeKey(email, assetId, tacticId);

      freshmarketerEventData['event_data']['composite_stitching_key'] = compositeKey;

      const clientId = getTrackerClientId() || getFormattedGAClientID();

      if (clientId) {
        freshmarketerEventData['event_data']['ga_client_id'] = clientId;
      }
      if (tacticId) {
        freshmarketerEventData['event_data']['allocadia_tactic_id'] = tacticId;
      }
      payload = {
        data: fmobj,
        event: freshmarketerEventData.event_data
      };
      if (postToKinesis) {
        localStorage.setItem('kinesisFmCollectorPost', JSON.stringify(payload));
      }
      postSuccess();
    } else {
      log(
        'No asset id is configured', 'warning',
        'No Asset Id', 0, { product, formClassName: $form.getAttribute('class') }
      );
      postSuccess();
    }
    localStorage.setItem('kinesisPost', 'yes');
  } catch (e) {
    log(
      'SiteUtilites - ReferenceError: Freshmarketer Mandatory Custom Fields',
      'error',
      'FW.SiteUtilites.buildFreshmarketerObject',
      2,
      e
    );
    if (postToKinesis) localStorage.removeItem('kinesisFmCollectorPost');
    postSuccess();
  }
  return postToKinesis ? externalDeferrer : payload;
};

const passPathFactoryParamsToFmarketer = (obj) => {
  const pathfactoryIds = ['pf_contact_external_id', 'pf_content_download_external_id'];
  const pfContactKeys = ['cf_product', 'cf_subproduct', 'cf_source', 'cf_engagementstatus', 'cf_marketingcampaign', 'cf_campaigndate', 'cf_campaign', 'cf_oproute', 'cf_mqlcreateddate', 'cf_region', 'cf_recentnote'];
  const pfContentKeys = ['cf_product', 'cf_subproduct', 'cf_source', 'cf_engagementstatus', 'cf_marketingcampaign', 'cf_campaigndate', 'cf_campaign', 'cf_oproute', 'cf_mqlcreateddate', 'cf_region', 'cf_recentnote'];

  for (const k of pathfactoryIds) {
    const parsedVal = getParameterByName(k);
    if (parsedVal) {
      const parsedCf = parsedVal.split(';');
      let customKeys = [];
      if (k === 'pf_contact_external_id') {
        customKeys = pfContactKeys;
      } else if (k === 'pf_content_download_external_id') {
        customKeys = pfContentKeys;
      }
      for (var i = 0; i < customKeys.length; i++) {
        if (parsedCf[i]) {
          obj.freshmarketerObject.custom_field[customKeys[i]] = parsedCf[i];
        }
      }
    }
  }
};

const filterFmCustomfields = (obj, newobj, $form) => {
  const partnerSignup = $form && $form.hasClass('partner-reseller-signupform');
  for (const cf in obj.freshmarketerObject.custom_field) {
    const lowerCaseCF = cf.toLowerCase();
    if (fmkeys[lowerCaseCF]) {
      newobj[fmkeys[lowerCaseCF]] = obj.freshmarketerObject.custom_field[cf];
      if (!(cf === 'cf_Fworksgenericsignupreferrer' || (partnerSignup && cf === 'cf_recentnote'))) {
        delete obj.freshmarketerObject.custom_field[cf];
      }
    }
  }
};

const passMarketplaceIntegrationToFmarketer = (obj) => {
  const attReferrer = localStorage.getItem('att_referrer');
  if (attReferrer) {
    obj.freshmarketerObject.att_referrer = attReferrer;
    localStorage.removeItem('att_referrer');
  }
  const shopifyJWT = getParameterByName('shopify_jwt');
  if (shopifyJWT) {
    obj.freshmarketerObject.oauth_provider = 'shopify';
  }
};

const passTagsToFmarketer = (obj) => {
  const tags = getParameterByName('utm_tags');
  if (tags) {
    obj.freshmarketerObject.tags = tags.split(',');
  }
};

const correctMarketingField = (data, key) => {
  try {
    const tableEntry = cfMarketingLookupTable[key];
    if (tableEntry) {
      data[tableEntry] = data[key];
      delete data[key];
    }
  } catch (e) {
    console.log('Table lookup error', e);
  }
};

const restructureFmObj = (obj, $form) => {
  const fmobj = {
    unique_identifier: { emails: '' },
    contact: {
      custom_field: {}
    }
  };
  if (obj.freshmarketerObject.email) {
    fmobj['unique_identifier']['emails'] = obj.freshmarketerObject.email;
    delete obj.freshmarketerObject.email;
  }
  if (obj.freshmarketerObject.custom_field && Object.keys(obj.freshmarketerObject.custom_field).length) {
    for (const key in obj.freshmarketerObject.custom_field) {
      if (key.includes('cf_')) {
        if (key === 'cf_update_list_id') {
          fmobj['contact']['custom_field'][key] = obj.freshmarketerObject.custom_field[key];
        } else if (key === 'cf_Fworksgenericsignupreferrer') {
          fmobj['contact']['custom_field']['cf_cf_fworksgenericsignupreferrer'] = obj.freshmarketerObject.custom_field[key].substring(0, 255);
        } else {
          const lowercaseKey = key.toLowerCase();
          const currentVal = obj.freshmarketerObject.custom_field[key];
          if (currentVal && typeof currentVal === 'string') {
            const truncatedVal = (currentVal.split('?').length === 2) ? currentVal.split('?')[0] : currentVal;
            if (lowercaseKey === 'cf_title' || lowercaseKey === 'cf_jobtitle') {
              fmobj['contact']['job_title'] = truncatedVal;
            } else if (lowercaseKey === 'cf_cf_mailing_address') {
              fmobj['contact']['custom_field']['cf_mailing_address'] = truncatedVal;
            } else {
              fmobj['contact']['custom_field'][`cf_${lowercaseKey}`] = truncatedVal;
            }
          } else {
            if (lowercaseKey === 'cf_title' || lowercaseKey === 'cf_jobtitle') {
              fmobj['contact']['job_title'] = String(currentVal);
            } else if (lowercaseKey === 'cf_cf_mailing_address') {
              fmobj['contact']['custom_field']['cf_mailing_address'] = String(currentVal);
            } else {
              fmobj['contact']['custom_field'][`cf_${lowercaseKey}`] = String(currentVal);
            }
          }
        }
      }
    }
    delete obj.freshmarketerObject.custom_field;
  }
  for (const key in obj.freshmarketerObject) {
    const lowercaseKey = key.toLowerCase();
    if (lowercaseKey === 'company') {
      fmobj['contact']['custom_field'][`cf_${lowercaseKey}`] = obj.freshmarketerObject[key];
    } else {
      fmobj['contact'][lowercaseKey] = obj.freshmarketerObject[key];
    }
  }
  if (fmobj.contact.hasOwnProperty('phone')) {
    let phoneNum = '';
    if (fmobj.contact.phone) {
      phoneNum = fmobj.contact.phone.trim();
    }
    fmobj.contact.work_number = phoneNum.length < 5 ? null : phoneNum;
    delete fmobj.contact.phone;
  }

  if ($form && $form.is('[class*="-signupform"]')) {
    let spam = false;
    const signupInitiateString = sessionStorage.getItem('signupInitiateTime');
    const signupInitiate = signupInitiateString ? new Date(signupInitiateString) : null;

    const signupStartString = localStorage.getItem('signupStartTime');
    const signupStart = signupStartString ? new Date(signupStartString) : null;

    if (signupInitiate && signupStart) {
      const differenceInMilliseconds = signupStart.getTime() - signupInitiate.getTime();
      const signupStartDifference = differenceInMilliseconds / 1000;
      if (signupStartDifference <= 25) {
        spam = true;
      }
    }
    fmobj.contact.spam = spam;
  }

  // Correct FM custom values
  for (const key in fmobj.contact.custom_field) {
    correctMarketingField(fmobj.contact.custom_field, key);
  }

  return fmobj;
};

const alterCampaignData = (data) => {
  $('.custom-inputs.type-freshmarketer_overrides').each((index, elem) => {
    const key = $(elem).attr('name');
    const value = $(elem).attr('data-custom-value');
    if (key && value) {
      const newKey = 'cf_' + key.toLowerCase().split(' ').join('');
      data.freshmarketerObject.custom_field[newKey] = value;
    }
  });
};

const freshmarketerPost = (obj, triggerCompleteEvent, form, postToCollector = true) => {
  const $form = $(form);
  const fmEventCustomInputs = {};
  const $freshmarketerCustomInputs = $form.find('.custom-inputs.type-freshmarketer');
  let payload;
  if ($freshmarketerCustomInputs.length > 0) {
    customHiddenFormFields($freshmarketerCustomInputs, obj);
  }
  const externalDeferrer = $.Deferred();
  // This will control if the external deferrer can be resolved or not.
  const postStatus = {
    event: false
  };
  const pageProduct = $('body').attr('data-pageProduct-name');
  // Check for Asset ID on the Form
  let fmAssetId = $form.find('.fm-asset-id').val() || $form.data('fm_asset_id') || 3649313;

  // Custom outreach checkbox, override campaign markers when checked.
  const $fieldOverrideCheckbox = $form.find('.field-override-checkbox');
  const isChecked = $fieldOverrideCheckbox.length > 0 && $fieldOverrideCheckbox.is(':checked');
  if (isChecked) {
    // alter asset id and campaign markers.
    alterCampaignData(obj);

    // override asset id, and cleanup
    const $fmOverrideAssetId = $form.find('.fm_override_asset_id');
    if ($fmOverrideAssetId) {
      fmAssetId = $fmOverrideAssetId.attr('value');
    }
  }

  // Freshmarketer Custom event  data - start
  // This function is used to post leads to freshmarketer. If the ajax call fails we trigger events so that other listeners (Submit handlers) can continue with their flow.
  const freshmarketerLeadPost = (obj) => {
    try {
      // Add custom field object if it doesn't exist
      if (!obj.freshmarketerObject.custom_field) {
        obj.freshmarketerObject.custom_field = {};
      }

      // Adding Lists Array only if there was a valid list ID.
      const listId = obj.freshmarketerObject.custom_field && obj.freshmarketerObject.custom_field.cf_freshmarketer_list;
      const utmMedium = getUtmMedium();
      if (listId) {
        obj.freshmarketerObject.custom_field['cf_update_list_id'] = listId;
        obj.freshmarketerObject.list_ids = [listId];
        obj.freshmarketerObject.append_values = ['list_ids'];
      }
      // Send the product for every lead. Check for overrides first and if product is mentioned in the override send that instead of taking it from the default product data attribute.
      if (obj.freshmarketerObject.overrides && obj.freshmarketerObject.overrides.product) {
        const productOverride = obj.freshmarketerObject.overrides.product;
        obj.freshmarketerObject.custom_field[`cf_${productOverride}`] = 'true';
      } else {
        if (obj.freshmarketerObject.custom_field) {
          obj.freshmarketerObject.custom_field[`cf_${pageProduct}`] = 'true';
        }
      }
      // Send email hash
      if (obj.freshmarketerObject.email) {
        window.FM && window.FM.associateVisitor && window.FM.associateVisitor(obj.freshmarketerObject.email);
        const emailHash = getShaHash(obj.freshmarketerObject.email);
        obj.freshmarketerObject.custom_field['cf_ehash'] = emailHash;
      }

      // Send Field only for Germany, Austria and Switzerland
      const gdprDoubleOptInCountries = ['Germany', 'Austria', 'Switzerland'];
      if (obj.freshmarketerObject.country && gdprDoubleOptInCountries.includes(obj.freshmarketerObject.country)) {
        obj.freshmarketerObject.custom_field['cf_gdprdoubleopt-in'] = obj.freshmarketerObject.contactable;
      }
      obj.freshmarketerObject.custom_field['cf_Fworksgenericsignupreferrer'] = Cookie.get('fw_sflu') || window.location.href || '';
      obj.freshmarketerObject.custom_field['cf_tacticid'] = getTacticID();
      // Remove the overrides object that the backend doesn't expect.
      obj.freshmarketerObject.overrides && delete obj.freshmarketerObject.overrides;
      if (utmMedium) {
        obj.freshmarketerObject.medium = utmMedium;
      }
      passPathFactoryParamsToFmarketer(obj);
      passMarketplaceIntegrationToFmarketer(obj);
      passTagsToFmarketer(obj);
      filterFmCustomfields(obj, fmEventCustomInputs, $form);
      const fmobj = restructureFmObj(obj, $form);

      if (!fmAssetId || window.location.pathname === '/ko/company/partners/partner-program-signup/') {
        log(
          'No asset id is configured', 'warning',
          'No Asset Id', 0, { pageProduct, formClassName: $form.getAttribute('class') }
        );
        postStatus.event = true;
        return;
      }

      const pathfactoryAssetIdArr = getParameterByName('pf_content_download_asset_id') || getParameterByName('pf_contact_asset_id');
      if (pathfactoryAssetIdArr) {
        const parsedCf = pathfactoryAssetIdArr.split(';');
        if (parsedCf.length) {
          fmAssetId = parsedCf[0];
        }
      }
      const product = (fmobj.contact && fmobj.contact.product) || null;
      const freshmarketerEventData = {
        email: fmobj.unique_identifier && fmobj.unique_identifier.emails,
        event_data: {
          email_id: fmobj.unique_identifier && fmobj.unique_identifier.emails,
          allocadia_asset_id: fmAssetId,
          domainUrl: (fmobj.contact && fmobj.contact.domainurl) || null,
          product,
          accountId: (fmobj.contact && fmobj.contact.accountid) || null,
          composite_stitching_key: (fmobj.contact && fmobj.contact.composite_stitching_key) || null
        }
      };
      if (listId) {
        freshmarketerEventData['event_data']['list_id'] = listId;
      }
      if ($form.data('fm_asset_id') && $form.find('[data-signup-product]').length) {
        freshmarketerEventData['event_data']['allocadia_asset_id'] = $form.data('fm_asset_id');
      }
      for (const cf in fmEventCustomInputs) {
        freshmarketerEventData['event_data'][cf] = fmEventCustomInputs[cf];
      }

      if (freshmarketerEventData['event_data']['recent_note']) {
        if (fmobj['contact']['custom_field'].cf_cf_crmnurture) freshmarketerEventData['event_data']['recent_note'] += ',' + fmobj['contact']['custom_field'].cf_cf_crmnurture;

        if (fmobj['contact']['custom_field'].hasOwnProperty('cf_cf_honcontact')) freshmarketerEventData['event_data']['recent_note'] += ',' + fmobj['contact']['custom_field'].cf_cf_honcontact || 'null';
        if (fmobj['contact']['custom_field'].hasOwnProperty('cf_cf_hoecomplat')) freshmarketerEventData['event_data']['recent_note'] += ',' + fmobj['contact']['custom_field'].cf_cf_hoecomplat || 'null';

        if (fmobj['contact']['custom_field'].cf_cf_questionwithchoices) freshmarketerEventData['event_data']['recent_note'] += ',' + fmobj['contact']['custom_field'].cf_cf_questionwithchoices;

        // Hotfix for holiday offer page. -> https://content-fw.freshworks.com/crm/lp/holiday-na-offer, https://content-fw.freshworks.com/crm/lp/holiday-row-offer
        const recentNoteArr = freshmarketerEventData['event_data']['recent_note'].split(',');
        if (recentNoteArr.length === 4) { // The condition is not to disturb the existing forms.
          recentNoteArr.push(recentNoteArr.shift());
          freshmarketerEventData['event_data']['recent_note'] = recentNoteArr.join(',');
        }
      }
      const keywordCookie = Cookie.get('fw_ue_utmkey');
      if (keywordCookie) {
        freshmarketerEventData['event_data']['keyword'] = keywordCookie;
      }

      freshmarketerEventPost(freshmarketerEventData, fmobj);
    } catch (e) {
      log(
        'SiteUtilites - ReferenceError: Freshmarketer Mandatory Custom Fields',
        'error',
        'FW.SiteUtilites.freshmarketerPost',
        2,
        e
      );
      postStatus.event = true;
    }
  };

  const freshmarketerEventPost = (obj, leadObj) => {
    const zargetFallbackUserId = +new Date() + 'r' + Math.random();
    obj['event_data']['zarget_user_id'] = Cookie.get('zarget_user_id') || zargetFallbackUserId;
    obj['event_data']['allocadia_page_url'] = window.location.href;
    const customCheckboxArray = [];
    $form.find('input[name="checkbox freshchat-whatsapp-signup-form"]:checked').each(function () {
      customCheckboxArray.push($(this).val());
    });
    const customCheckboxValues = customCheckboxArray.join(';');
    if (customCheckboxValues) {
      obj['event_data']['signup_channel'] = customCheckboxValues;
    }
    const tacticId = getTacticID();
    const email = (obj.email) ? obj.email : null;
    const assetId = (obj.event_data && obj.event_data.allocadia_asset_id) ? obj.event_data.allocadia_asset_id : null;
    const compositeKey = getCompositeKey(email, assetId, tacticId);

    if (!obj['event_data']['composite_stitching_key']) {
      obj['event_data']['composite_stitching_key'] = compositeKey;
    }

    const clientId = getTrackerClientId() || getFormattedGAClientID();
    if (clientId) {
      obj['event_data']['ga_client_id'] = clientId;
    }
    if (tacticId) {
      obj['event_data']['allocadia_tactic_id'] = tacticId;
    }
    payload = {
      data: leadObj,
      event: obj.event_data
    };

    const formSubmitEvent = new CustomEvent('formSubmitEvent', {
      detail: {
        payload,
        system: 'fmCollectorPostVulcan'
      }
    });
    window.dispatchEvent(formSubmitEvent);

    if (postToCollector) {
      localStorage.setItem('fmarketerStartTime', new Date().toISOString());
      try {
        jQuery.ajax({
          data: JSON.stringify(payload),
          method: 'POST',
          url: 'https://alfred.freshworks.com/v1/fm-collector-post',
          contentType: 'application/json',
          dataType: 'json',
          complete: () => {
            postStatus.event = true;
          }
        });
      } catch (e) {
        log(
          'SiteUtilites - ReferenceError: Freshmarketer custom event post failed',
          'error',
          'FW.SiteUtilites.freshmarketerEventPost',
          2,
          e
        );
        postStatus.event = true;
      }
    } else {
      postStatus.event = true;
    }
  };

  // This setInterval function will keep checking if all the events that need to be finished have completed. By default lead property needs to set to true. Conditionally events needs to be set to true.
  const successInterval = setInterval(() => {
    if (postStatus.event) {
      clearInterval(successInterval);
      if (triggerCompleteEvent === undefined || triggerCompleteEvent) {
        jQuery('body').trigger('freshmarketerPostCompleted');
      }
      if (postToCollector) externalDeferrer.resolve();
    }
  }, 100);

  freshmarketerLeadPost(obj);

  return postToCollector ? externalDeferrer : payload;
};

/*
 * Configure asset id manually for product signup forms
*/

const configureAssetId = ($form, product) => {
  const assetIds = {
    fdesk: 3496392,
    fservice: 3496393,
    fsales: 3496395,
    fchat: 3496396,
    fcaller: 3496397,
    fteam: 3496399,
    fmarketer: 3496400,
    frelease: 3496401,
    fconnect: 3496402,
    fping: 3496404,
    fstatus: 3496405,
    fwcrm: 4021272,
    fsurvey: 5223592,
    fwcssuite: 5991301
  };
  if (assetIds[product]) {
    const $assetId = $form.find('.fm-asset-id');
    if ($assetId.length) {
      $assetId.val(assetIds[product]);
    } else {
      $form.data('fm_asset_id', assetIds[product]);
    }
  }
};

const fwcrmAssetId = ($form, planId) => {
  const assetIds = {
    sc: '3496395',
    mc: '3496400',
    c4l: '4021272',
    suite: '4021272'
  };
  if (assetIds[planId]) {
    $($form).find('.fm-asset-id').val(assetIds[planId]);
    $($form).data('fm_asset_id', assetIds[planId]);
  }
};

/* 'GA1.2.621337473.1541400448'
    This is the value Cookie.get('_ga') returns. But digital marketing wants it in this format '621337473.1541400448' as they feel GA1.2. is redundant
*/
const sanitizeGACookie = (cookieValue) => {
  if (typeof cookieValue !== 'string') {
    return '';
  }
  cookieValue = cookieValue.replace(/GA\d\.\d\.(.+)/, '$1');
  return cookieValue;
};

/*
  * Use this function to remove trailing slashes at the end of url.
  * Notice that this will work only for strings ending with trailing slashes and would fail if * there are any query parameters.
*/
const removeTrailingSlashEnd = (string = '') => {
  if (typeof string !== 'string') {
    log(
      'SiteUtilites - ReferenceError: String is not passed as input',
      'error',
      'FW.SiteUtilites.removeTrailingSlashEnd',
      2,
      {}
    );
    return '';
  }
  const trailingSlashEndRegex = /(.+)\/$/gi;
  string = string.replace(trailingSlashEndRegex, '$1');
  return string;
};

// UID generate for spamscore validation from backend and email service team
const generateUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};
const classifyStatusCode = (statusCode, message, context, responseObject) => {
  let level = '';
  let severity = '';
  if (statusCode === 401 || statusCode === 404) {
    level = 'error';
    severity = '1';
  } else if (statusCode === 422) {
    level = 'warning';
    severity = '2';
  } else if (statusCode >= 500 || statusCode === 0 || statusCode === 403) {
    level = 'warning';
    severity = '0';
  }
  log(
    message + `StatusCode: ${statusCode}`,
    level,
    context,
    severity,
    responseObject
  );
};
const customFreshsalesScript = (script) => {
  window.freshsales = null;
  $('body').append(script);
};

const convertToHTMLAttribute = function (text) {
  try {
    return text.toLowerCase().trim().replace(/\s/g, '-');
  } catch (err) {
    log(
      `Error converting string to HTML attribute. Input was ${text}. Error message: ${err.message}`,
      `Exception`,
      `Input error in convertToHTMLAttribute utility`,
      2,
      ''
    );
    return text;
  }
};

/**
 * @description This function is used to reduce the rate at which event handlers are triggered by listeners
 * @param {number} delay - delay in milliseconds
 * @param {function} fn - callback function
 * */
const debounce = function (delay, fn) {
  let timerId;
  return function (...args) {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      fn(...args);
      timerId = null;
    }, delay);
  };
};

const getCurrentLocation = function () {
  var currentLoc;
  try {
    currentLoc = window.parent.location.href;
  } catch (e) {
    currentLoc = window.location.href;
  }
  return currentLoc;
};

const getCurrentReferrer = function () {
  var currentLoc = document.referrer;
  if (!currentLoc) {
    try {
      currentLoc = window.parent.location.href;
    } catch (e) {
      currentLoc = false;
    }
  }
  return currentLoc;
};
// This method shows form backend error corresponding to the status codes.
const setBackendErrorMessage = function ($form, statusCode, product) {
  // Error scenarios to be handled and shown to user
  const errorMap = {
    fservice: {
      supportText: 'Please contact <a href="mailto:support@freshservice.com" target="_top">support@freshservice.com</a>',
      errorCode: {
        400: 'Request content was invalid.',
        401: 'Unauthorized access.',
        403: "Forbidden resource can't be accessed.",
        700: 'An error occured.'
      }
    }
  };

  if (product && errorMap[product]) {
    $form.append(`<div class="backend-fail-note">${(errorMap[product]['errorCode'][statusCode] || errorMap[product]['errorCode'][700]) + ' ' + errorMap[product]['supportText']} </div>`);
  } else {
    $form.append('<div class="backend-fail-note">Something went wrong. please try after some time.</div>');
  }
};

/*
 * This method is used to find an element in an array contains regex
 * like [/[abc]/, /\/$/]
 */
const findItemInRegexArray = (searchTxt, regexArray = []) => {
  let isItemFound = false;
  for (var i = 0; i < regexArray.length; i++) {
    if (regexArray[i].test(searchTxt)) {
      isItemFound = true;
      break;
    }
  }
  return isItemFound;
};

/*
 * This tactic ID will get passed with freshmarketer when tactic_id is not found in the URL or based on below scenario
 */
const setTacticID = function () {
  const tacticIdSession = getTacticID();
  const tacticIdFromUrl = getParameterByName('tactic_id') || getParameterByName('utm_tactic_id');
  const tacticIdObject = {
    organic: '3463793',
    internal: '3463865',
    referral: '3463866',
    direct: '3463867'
  };
  const internalDomains = [/www\.freshworks\.com/, /^freshdesk\..+/, /^freshservice\..+/];
  const organicDomains = [/^search\.naver\.com/i, /^yandex\..+/i];
  // The below engine URLs will have different TLDs so we check from maxmind session object
  const organicSearchEngine = ['Yahoo', 'Google', 'Bing', 'Baidu'];
  let tacticID = tacticIdFromUrl || tacticIdObject.direct;
  if (!tacticIdSession) {
    let referrerHost;
    let referrerEngine;
    try {
      referrerHost = session.current_session.referrer_info.host;
      referrerEngine = session.current_session.search.engine;
    } catch (e) {
      // To avoid duplicate errors
      // Maxmind errors are already captured in an another place
    }
    if (!tacticIdFromUrl && referrerHost) {
      if (referrerHost === window.location.host || !referrerHost) {
        // Direct Referral - User types the URL directly from a address bar
        tacticID = tacticIdObject.direct;
      } else if (findItemInRegexArray(referrerHost, internalDomains)) {
        // Internal Referral - Referral from inside our websites eg : freshworks.com, freshdesk.com, ...etc
        tacticID = tacticIdObject.internal;
      } else if (organicSearchEngine.includes(referrerEngine)) {
        // Organic Referral - Referral from search engines like Google, Bing, ...etc
        tacticID = tacticIdObject.organic;
      } else if (findItemInRegexArray(referrerHost, organicDomains)) {
        // Organic Referral
        tacticID = tacticIdObject.organic;
      } else {
        // Non Organic Referral - Referral which not falls under Internal or Organic
        tacticID = tacticIdObject.referral;
      }
    }
    try {
      sessionStorage.setItem('tactic_id', tacticID);
    } catch (e) {
      // do nothing
    }
  } else if (tacticIdFromUrl && tacticIdSession !== tacticIdFromUrl) {
    try {
      sessionStorage.setItem('tactic_id', tacticIdFromUrl);
    } catch (e) {
      // do nothing
    }
  }
};
const getTacticID = function () {
  try {
    return sessionStorage.getItem('tactic_id');
  } catch (e) {
    return null;
  }
};

/**
 * Get Country code from country name
 */

const getCountryCode = function (country, countryName, productKey) {
  const countryList = {
    EU: [
      'Austria',
      'Belgium',
      'Cyprus',
      'Estonia',
      'Finland',
      'France',
      'Germany',
      'Greece',
      'Ireland',
      'Italy',
      'Latvia',
      'Luxembourg',
      'Malta',
      'Netherlands',
      'Portugal',
      'Slovakia',
      'Slovenia',
      'Spain',
      'Andorra',
      'Kosovo',
      'Montenegro',
      'Monaco',
      'San Marino',
      'The Vatican City',
      'Sweden',
      'Denmark',
      'Norway',
      'Finland',
      'Czech Republic',
      'Switzerland',
      'Liechtenstein'
    ],
    'SOUTH-AMERICA': [
      'Mexico',
      'Brazil',
      'Panama',
      'Costa Rica',
      'Argentina',
      'Chile',
      'Uruguay',
      'Paraguay',
      'Bolivia',
      'Peru',
      'Dominican Republic',
      'Colombia'
    ],
    APAC: [
      'Vietnam',
      'Philippines',
      'Indonesia',
      'Thailand'
    ],
    RU: [
      'Russia'
    ]
  };

  const countryCodeList = {
    AU: [
      'NZ',
      'NZL'
    ],
    BR: [
      'BR',
      'BRA'
    ]
  };

  let cName = country;
  for (const name in countryList) {
    if (countryList[name].indexOf(countryName) > -1) {
      cName = name;
      break;
    }
  }
  for (const countryCode in countryCodeList) {
    if (countryCodeList[countryCode].indexOf(country) > -1) {
      cName = countryCode;
      break;
    }
  }

  if (cName === 'BR' && productKey !== 'fdesk') {
    cName = 'SOUTH-AMERICA';
  }
  return cName;
};

/**
 * Get Country code from slug locale only if pricing format set to be locale
 */

const getLocaleCountryCode = () => {
  if (!$('[data-pricing-format = "locale"]').length) {
    return;
  }
  const localeCurrency = {
    US: ['latam', 'br', 'jp', 'ko', 'cn', 'th', 'vi', 'hk'],
    EU: ['eu', 'de', 'nl', 'fr', 'se', 'es', 'it', 'pl', 'tr'],
    RU: ['ru']
  };
  let countryCode;
  const pathMatch = /^(?:https?:\/\/)?[^/]+\/([^/]+)(?:\/|$)/.exec(window.location.href);
  if (pathMatch && pathMatch.length > 0) {
    const langCode = pathMatch[1];
    $.each(localeCurrency, function (key, value) {
      if (value.includes(langCode)) {
        countryCode = key;
      }
    });
  }
  return countryCode;
};

/**
 * Select a country code from country name and overwrite with locale country code,
 * If selected country code is not there is data return fallback US as countryCode
 */

function selectCountry (data, countryCode, countryName) {
  let country = getCountryCode(countryCode, countryName);
  const localeCountryCode = getLocaleCountryCode();
  if (localeCountryCode) {
    country = localeCountryCode;
  }
  return (country in data) ? country : 'US';
}

const addToPerformance = (key, value) => {
  const performanceObject = JSON.parse(localStorage.getItem('kinesisPerformance')) || {};
  performanceObject[key] = value;
  localStorage.setItem('kinesisPerformance', JSON.stringify(performanceObject));
};

const customHiddenFormFields = ($customFields, payloadRef) => {
  const dateObject = new Date();
  const dateInfo = {
    dateD: dateObject.getDate(),
    // Since getMonth() returns month from 0-11 not 1-12
    monthM: dateObject.getMonth() + 1,
    yearYYYY: dateObject.getFullYear()
  };
  $customFields.each((index, element) => {
    const $element = $(element);
    const key = $element.attr('name');
    // Return if there is no key.
    if (!key) return;
    const customKeyType = $element.data('custom-type');
    const customSearchValue = $element.data('custom-value');
    switch (customKeyType) {
      case 'static':
      case 'event_location_specific':
        $element.val(customSearchValue);
        break;
      case 'query_parameter':
        $element.val(getParameterByName(customSearchValue));
        break;
      case 'cookie':
        $element.val(Cookie.get(customSearchValue));
        break;
      case 'full_url':
        $element.val(window.location.href);
        break;
      case 'lead_space':
        // Leadspace confidence level value will be dynamically injected by the leadspace script
        break;
      case 'submission_date':
        $element.val([dateInfo.dateD, dateInfo.monthM, dateInfo.yearYYYY].join('-'));
        break;
      case 'submission_date_var1':
        $element.val([dateInfo.yearYYYY, dateInfo.monthM, dateInfo.dateD].join('-'));
        break;
      default:
        $element.val('');
    }
    const customKeyValue = $element.val();
    const isFmarketer = payloadRef.hasOwnProperty('freshmarketerObject');
    // Handle edge case temporarily. If key is Product nest it under a field called deal.
    if (key === 'Product' && !isFmarketer) {
      payloadRef.deal = {};
      payloadRef['deal'][key] = customKeyValue;
      return;
    }
    if (isFmarketer) {
      const freshmarketerKey = 'cf_' + key.replace(/\s+/gi, '');
      if (!payloadRef['freshmarketerObject'].hasOwnProperty('custom_field')) {
        payloadRef['freshmarketerObject']['custom_field'] = {};
      }
      payloadRef['freshmarketerObject']['custom_field'][freshmarketerKey] = customKeyValue;
    } else {
      payloadRef[key] = customKeyValue;
    }
  });
};

/**
 * Function to escape unsafe characters to prevent XSS
 */

const escapeHtml = text => {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, m => map[m]);
};

/*
 * Post signup error log to Lambda micro-service
 */

const signupErrorLog = (obj, form) => {
  const $form = $(form);
  obj['referrer'] = window.location.href;
  obj['environment'] = __NODE_ENV__ || 'production';
  if (!obj.product) {
    obj['product'] = $form.data('product') || $('body').attr('data-product-name');
  }
  if (!obj.email) {
    obj.email = $form.find('.email-form').val();
  }

  try {
    jQuery.ajax({
      data: JSON.stringify(obj),
      method: 'POST',
      url: 'https://alfred.freshworks.com/v1/signup-data-backup',
      contentType: 'application/json',
      dataType: 'text'
    });
  } catch (e) {
  }
};

/*
 * Post signup log to Lambda micro-service
 */

const signupLog = (obj, form, status, message, isBeaconRequest = false) => {
  const $form = $(form);
  obj['referrer'] = window.location.href;
  obj['product'] = $form.data('product') || $('body').attr('data-product-name');
  if (!obj.email) {
    obj.email = $form.find('.email-form').val();
  }
  obj['status'] = status;
  obj['message'] = message;
  const url = 'https://alfred.freshworks.com/v1/signup-log';
  try {
    if (isBeaconRequest && canIUseSendBeacon()) {
      navigator.sendBeacon(url, JSON.stringify(obj));
    } else {
      jQuery.ajax({
        data: JSON.stringify(obj),
        method: 'POST',
        url,
        contentType: 'application/json',
        dataType: 'text'
      });
    }
  } catch (e) {
  }
};

/*
 * Post signup performance log to Lambda micro-service
 */

const signupPerformanceLog = (obj, product, status, code, msg) => {
  obj['system'] = 'vulcan';
  obj['product'] = product || $('body').attr('data-product-name');
  obj['signup_status'] = status || localStorage.getItem('signupStatusText');
  obj['signup_code'] = code || localStorage.getItem('signupStatusCode');
  obj['signup_msg'] = msg || localStorage.getItem('signupStatusMsg');
  obj['bundle_id'] = localStorage.getItem('bundleId') || null;
  obj['signup_url'] = localStorage.getItem('signupPerformanceSignupUrl');
  obj['referrer'] = document.referrer;
  obj['signup_landing'] = localStorage.getItem('signupLandingTime') || sessionStorage.getItem('signupLandingTime') || null;
  obj['is_sso_with_business_email'] = localStorage.getItem('isSSOWithBusinessEmail') || 'false';
  obj['is_prefilled_form'] = localStorage.getItem('isPrefilledForm') || 'false';
  obj['signup_initiate'] = localStorage.getItem('signupInitiateTime') || null;
  obj['signup_start'] = localStorage.getItem('signupStartTime') || null;
  obj['aloha_start'] = localStorage.getItem('alohaStartTime') || null;
  obj['aloha_end'] = localStorage.getItem('alohaEndTime') || null;
  obj['upstream_service_duration'] = localStorage.getItem('upstreamServiceDuration') || null;
  obj['fmarketer_start'] = localStorage.getItem('fmarketerStartTime') || null;
  obj['fmarketer_end'] = localStorage.getItem('fmarketerEndTime') || null;
  obj['gtm_script_load_start'] = localStorage.getItem('gtmScriptLoadStartTime') || null;
  obj['gtm_script_load_end'] = localStorage.getItem('gtmScriptLoadEndTime') || null;
  obj['thankyou_redirect_start'] = localStorage.getItem('thankyouRedirectStartTime') || null;
  obj['product_redirect_start'] = localStorage.getItem('productRedirectStartTime') || null;
  obj['getstarted_btn_display_start'] = localStorage.getItem('getStartedBtnDisplayTime') || null;
  obj['is_honeypot_signup'] = localStorage.getItem('isHoneyPotSignup') || 'false';
  obj['elapsed_time_getstartedbtn_since_thanku'] = (
    obj['getstarted_btn_display_start'] && obj['thankyou_redirect_start']) ? (
      new Date(obj['getstarted_btn_display_start']).getTime() - new Date(obj['thankyou_redirect_start']).getTime()) / 1000 : null;
  obj['elapsed_time_thankuredirect_since_alohaend'] = (obj['aloha_end'] && obj['thankyou_redirect_start']) ? (
    new Date(obj['thankyou_redirect_start']).getTime() - new Date(obj['aloha_end']).getTime()) / 1000 : null;
  obj['elapsed_time_prodredirect_since_thanku'] = (obj['product_redirect_start'] && obj['thankyou_redirect_start']) ? (
    new Date(obj['product_redirect_start']).getTime() - new Date(obj['thankyou_redirect_start']).getTime()) /
    1000 : null;
  obj['elapsed_time_thankuredirect_since_signup'] = (obj['thankyou_redirect_start'] && obj['signup_start']) ? (
    new Date(obj['thankyou_redirect_start']).getTime() - new Date(obj['signup_start']).getTime()) / 1000 : null;
  obj['elapsed_time_getstartedbtn_since_signup'] = (obj['getstarted_btn_display_start'] && obj['signup_start']) ? (
    new Date(obj['getstarted_btn_display_start']).getTime() - new Date(obj['signup_start']).getTime()) / 1000 : null;
  obj['elapsed_time_fmarketerend_since_fmarketerstart'] = (obj['fmarketer_end'] && obj['fmarketer_start']) ? (
    new Date(obj['fmarketer_end']).getTime() - new Date(obj['fmarketer_start']).getTime()) / 1000 : null;
  obj['elapsed_time_alohastart_since_signup'] = (obj['aloha_start'] && obj['signup_start']) ? (
    new Date(obj['aloha_start']).getTime() - new Date(obj['signup_start']).getTime()) / 1000 : null;
  obj['elapsed_time_alohaend_since_alohastart'] = (obj['aloha_end'] && obj['aloha_start']) ? (
    new Date(obj['aloha_end']).getTime() - new Date(obj['aloha_start']).getTime()) / 1000 : null;
  if (product === 'fservice') {
    obj['fservice_api_start'] = localStorage.getItem('fserviceSignupStartTime') || null;
    obj['fservice_api_end'] = localStorage.getItem('fserviceSignupEndTime') || null;
    obj['elapsed_time_fservicestart_since_signup'] = (obj['fservice_api_start'] && obj['signup_start']) ? (
      new Date(obj['fservice_api_start']).getTime() - new Date(obj['signup_start']).getTime()) / 1000 : null;
    obj['elapsed_time_fserviceend_since_fservicestart'] = (obj['fservice_api_end'] && obj['fservice_api_start']) ? (
      new Date(obj['fservice_api_end']).getTime() - new Date(obj['fservice_api_start']).getTime()) / 1000 : null;
    obj['elapsed_time_thankuredirect_since_fserviceend'] = (
      obj['thankyou_redirect_start'] && obj['fservice_api_end']) ? (
        new Date(obj['thankyou_redirect_start']).getTime() - new Date(obj['fservice_api_end']).getTime()) / 1000 : null;
  }
  resetSignupPerformanceLog();
  try {
    jQuery.ajax({
      data: JSON.stringify(obj),
      method: 'POST',
      url: 'https://alfred.freshworks.com/v1/signup-performance-log',
      contentType: 'application/json',
      dataType: 'text'
    });
  } catch (e) {
  }
};

const resetSignupPerformanceLog = () => {
  ['signupLandingTime', 'signupInitiateTime', 'signupStartTime', 'alohaStartTime', 'alohaEndTime', 'upstreamServiceDuration', 'fmarketerStartTime', 'fmarketerEndTime', 'thankyouRedirectStartTime', 'productRedirectStartTime', 'signupPerformanceLogData', 'signupPerformanceProductLabel', 'signupPerformanceSignupUrl', 'signupStatusText', 'signupStatusCode', 'signupStatusMsg', 'fserviceSignupStartTime', 'fserviceSignupEndTime', 'getStartedBtnDisplayTime', 'gtmScriptLoadStartTime', 'gtmScriptLoadEndTime', 'bundleId', 'isHoneyPotSignup', 'isSSOWithBusinessEmail', 'isPrefilledForm'].forEach((item) => {
    localStorage.removeItem(item);
  });
};

/*
 * Utilities - signup performance log
 */
const triggersignupPerformanceLogApi = (storeProductRedirectStart = false) => {
  if (storeProductRedirectStart) {
    localStorage.setItem('productRedirectStartTime', new Date().toISOString());
  }
  const signupPerformanceLogData = localStorage.getItem('signupPerformanceLogData');
  const productLabel = localStorage.getItem('signupPerformanceProductLabel');
  if (!signupPerformanceLogData) return;
  signupPerformanceLog(JSON.parse(signupPerformanceLogData), productLabel);
};

/*
 * Composite stitching key is formed for fmarketer post
 */

const getCompositeKey = (email, assetId, tacticId) => {
  const currentTimestamp = new Date().getTime() * 1000;
  return `${email || null}~${assetId || null}~${tacticId || null}`.replace(/null/g, '') + `~${currentTimestamp}`;
};

/*
 * Valid Ga Client id is parsed from session
 */

const getGAClientID = () => {
  return Cookie.get('_ga') || '';
};

/*
* Get GA Client Id from tracker
*/
const getTrackerClientId = () => {
  let clientId = '';
  try {
    var trackers = ga.getAll();
    for (var t = 0; t < trackers.length; t++) {
      if (trackers[t].get('clientId')) {
        clientId = trackers[t].get('clientId');
        break;
      }
    }
  } catch (e) {
    console.log(e);
  }
  return clientId;
};

/*
 * Method to post leads to freshsales
 */

const freshsalesPost = (obj, showTrigger) => {
  const deferrer = $.Deferred();
  if (obj.lead.custom_field.cf_partner_type && obj.lead.custom_field.cf_partner_type === 'Technology Partner') {
    return deferrer.resolve();
  }
  try {
    jQuery.ajax({
      data: JSON.stringify(obj),
      method: 'POST',
      url: 'https://alfred.freshworks.com/v1/freshsales-post',
      contentType: 'application/json',
      dataType: 'json',
      complete: () => {
        if (showTrigger === undefined || showTrigger) {
          jQuery('body').trigger('freshsalesPostCompleted');
        }
        deferrer.resolve();
      }
    });
    return deferrer;
  } catch (e) {
    log(
      'SiteUtilites - ReferenceError: Freshsales Mandatory Custom Fields',
      'error',
      'FW.SiteUtilites.freshsalesPost',
      2,
      e
    );
    if (showTrigger === undefined || showTrigger) {
      jQuery('body').trigger('freshsalesPostCompleted');
    }
    deferrer.resolve();
  }
};

const freshsalesPostV2 = (obj, showTrigger) => {
  const deferrer = $.Deferred();
  try {
    jQuery.ajax({
      data: JSON.stringify(obj),
      method: 'POST',
      url: 'https://alfred.freshworks.com/v1/freshsales-post-v3',
      contentType: 'application/json',
      dataType: 'json',
      complete: (jqXHR) => {
        const responseObj = {
          statusCode: jqXHR.status,
          message: jqXHR.responseText
        };
        if (showTrigger === undefined || showTrigger) {
          jQuery('body').trigger('freshsalesPostCompleted');
        }
        deferrer.resolve(responseObj);
      }
    });
    return deferrer;
  } catch (e) {
    log(
      'SiteUtilites - ReferenceError: Partner sign up form Fsales post v2 failed',
      'error',
      'FW.SiteUtilites.freshsalesPostV2',
      2,
      e
    );
    deferrer.resolve();
  }
};

/*
 * Method to create freshmarketer transactional emails
 */

const sendTransactionalEmail = (form, { freshmarketerObject }, triggerCompleteEvent) => {
  const $form = $(form);
  const payload = {
    email: $form.find('.email-form').val(),
    templateId: $form.data('transactional-email-id')
  };

  if (freshmarketerObject) {
    Object.keys(freshmarketerObject).forEach(key => {
      const value = freshmarketerObject[key];
      if (typeof value === 'object') {
        Object.keys(value).forEach(customKey => {
          payload[customKey] = value[customKey];
        });
      } else {
        payload[key] = value;
      }
    });
  }

  const $customFields = $form.find('.transaction-email-custom-fields');
  if ($customFields.length > 0) {
    $customFields.each((index, element) => {
      const $input = $(element);
      payload[`te_${$input.attr('name')}`] = $input.val();
    });
  }

  try {
    jQuery.ajax({
      data: JSON.stringify(payload),
      method: 'POST',
      url: 'https://alfred.freshworks.com/v1/transactional-email',
      contentType: 'application/json',
      dataType: 'json',
      complete: () => {
        triggerCompleteEvent && jQuery('body').trigger('transactionalEmailCompleted');
      }
    });
  } catch (e) {
    log(
      'SiteUtilites - ReferenceError: Transactional email post failed',
      'error',
      'FW.SiteUtilites.sendTransactionalEmail',
      2,
      e
    );
    triggerCompleteEvent && jQuery('body').trigger('transactionalEmailCompleted');
  }
};

/*
 * Method which returns mobile app download links
 */

const getAppLink = (link, type) => {
  const attributes = {
    Android: {
      src: '/static-assets/images/common/icons/img-google-play.png',
      alt: 'Google play link'
    },
    iOS: {
      src: '/static-assets/images/common/icons/img-apple-store.png',
      alt: 'Apple store link'
    }
  };
  return attributes[type]
    ? `<a class="app-btn" href=${link} target="_blank"  rel="noopener"><img class="app-store-img" src="${attributes[type].src}" alt"${attributes[type].alt}" /></a>`
    : '';
};

const getCombinedGaClientIdWithoutGclid = (compositekey) => {
  return `${Cookie.get('_ga') || getTrackerClientId()}:${compositekey}`;
};

const setGclId = function () {
  // Binding Google Click ID to localstorage
  const getExpiryRecord = function (value) {
    const expiryPeriod = 7776000000; // 90 day expiry in milliseconds (90 * 24 * 60 * 60 * 1000)

    const expiryDate = new Date().getTime() + expiryPeriod;
    return { value, expiryDate };
  };

  const addGclId = function () {
    const gclidParam = getParameterByName('gclid');
    let gclidRecord = null;

    const gclsrcParam = getParameterByName('gclsrc');
    const isGclsrcValid = !gclsrcParam || gclsrcParam.indexOf('aw') !== -1;

    if (gclidParam && isGclsrcValid) {
      gclidRecord = getExpiryRecord(gclidParam);
      localStorage.setItem('gclid', JSON.stringify(gclidRecord));
    }
  };

  addGclId();
};

const getGclId = function () {
  const gclid = JSON.parse(localStorage.getItem('gclid'));
  return gclid ? gclid.value : '';
};

const passSignupResponseToGA = (accountId, domain, product) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(
    { event: 'Signup AccountId', account_id: accountId },
    { event: 'Signup Domain', domain },
    { event: 'Signup Product', product }
  );
};

const getCombinedGaClientId = function (gclid, compositeKey) {
  return `${Cookie.get('_ga') || getTrackerClientId() || ''}~${gclid}:${compositeKey}`;
};

const GDPRFunctionalCookieDisabled = () => {
  if (window.OptanonActiveGroups && window.OptanonActiveGroups.includes('C0003')) {
    return false;
  } else {
    return true;
  }
};

// Event triggered via keyboard key
const keyTriggered = (e, keyName) => {
  if (!e || !keyName) return false;
  const keyD = (e.key !== undefined) ? e.key : e.keyCode;
  const isEnter = (keyD === 'Enter') || (keyD === 13);
  const isSpacebar = (['Spacebar', ' '].indexOf(keyD) >= 0) || (keyD === 32);
  const isSubmit = isEnter || isSpacebar;
  let output = false;

  switch (keyName) {
    case 'SPACEBAR': {
      output = isSpacebar;
      break;
    }
    case 'ENTER': {
      output = isEnter;
      break;
    }
    case 'SUBMIT': {
      output = isSubmit;
      break;
    }
  }
  return (output || false);
};

/**
 * Returns promise object in resolved state
 *
 * @param {function} promiseCall The function executes.
 * @param {object} promiseObj The object stores response.
 * @return {Promise}
 */
const returnResolvedPromise = (promiseCall, promiseObj = {}) => {
  return new Promise((resolve, reject) => {
    promiseCall.then((response) => {
      promiseObj.resolved = response;
      resolve();
    }).catch((err) => {
      promiseObj.rejected = err;
      resolve();
    });
  });
};

const getLinkedAccounts = (product, region, token) => {
  const payload = {
    product,
    region,
    token
  };

  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };
  return makeApiCall({
    body: JSON.stringify(payload),
    method: 'POST',
    headers,
    url: 'https://alfred.freshworks.com/v1/get-linked-accounts'
  });
};

const getProductIconInfo = product => {
  const iconMap = {
    fdesk: {
      src: '/static-assets/images/common/company/logos/support-desk.svg',
      alt: 'Support Desk'
    },
    fdeskOmni: {
      src: '/static-assets/images/common/company/logos/omnichannel-suite.svg',
      alt: 'Omnichannel Suite'
    }
  };
  return iconMap[product];
};

const isBlockedDomain = (domain, blockedDomains) =>
  blockedDomains.some(blockedDomain =>
    // domain or subdomain match
    domain === blockedDomain || domain.endsWith(`.${blockedDomain}`)
  );

export const isShopifySignup = () => !!getParameterByName('shopify_jwt');

export const isGoogleSSO = (form) => {
  const JWTToken = $(form).find('.google-jwt-token').val();
  return !!(JWTToken && JWTToken.length);
};

const isBusinessEmail = (email = '', element) => {
  const form = $(element).closest('form');
  // Skip restricted domain check for Shopify signup and Google SSO
  if (isShopifySignup() || isGoogleSSO(form)) return true;

  let isDemoForm = false;
  $(form).find('.custom-inputs.type-freshmarketer').each(function () {
    const name = $(this).attr('name');
    const customValue = $(this).data('custom-value');
    if (name && customValue) {
      if ((name.toLowerCase() === 'source' && customValue.toLowerCase() === 'demo request') || (name.toLowerCase() === 'campaign' && customValue.toLowerCase() === 'demo request')) {
        isDemoForm = true;
      }
    }
  });
  const productName = document.body.getAttribute('data-product-name');
  const emailDomain = (email.split('@')[1] || '').toLowerCase();
  if (!emailDomain) return false;
  const { all_products: allProducts = [], fservice = [] } = RestrictedDomains.restrictedDomains;
  if (isDemoForm) {
    const demo = fservice;
    demo.push('gmail.com');
    if (isBlockedDomain(emailDomain, demo)) return false;
  } else if (isBlockedDomain(emailDomain, allProducts) || (productName === 'fservice' && isBlockedDomain(emailDomain, fservice))) {
    return false;
  }
  return true;
};

const isTextHTML = (text) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');
  return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
};

const showHideFields = (element = '', display = 'none') => {
  if (element) {
    element.css('display', display);
  }
};

const additionalGenericFields = (element, form) => {
  if (element && element.data('generic-validate-on-blur')) {
    if (element.val() && element.valid() && !isBusinessEmail(element.val())) {
      showHideFields(form.find('.job-level-form, .website-form, .company-industry-form').parent(), 'block');
      GA4Utilities.expandedFormEvent('Additional fields shown');
    } else {
      showHideFields(form.find('.job-level-form, .website-form, .company-industry-form').parent(), 'none');
    }
  }
};

// A function which returns methods to handler channel information for signups
const channelInfo = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const utmParams = {};
  /**
   * 1. Look at utm query parameters first for the channel attributes
   * 2. If the UTM query params are empty, check document.referer
   * 3. Assign "direct | none" if document.referrer is empty
   */
  if (!queryParams.get('utm_source')) {
    switch (true) {
      case (/freshworks.com|freshdesk.com|freshservice.com/.test(document.referrer) || !document.referrer):
        utmParams['direct'] = 'none';
        break;
      case /google.com/.test(document.referrer):
        utmParams['google'] = 'organic';
        break;
      default:
        utmParams[document.referrer] = 'referrer';
        break;
    }
  } else {
    utmParams[queryParams.get('utm_source')] = queryParams.get('utm_medium');
  }

  const _getStoredSourceMediums = () => {
    let sourceMediums = null;
    try {
      sourceMediums = JSON.parse(localStorage.getItem('source_medium'));
    } catch (e) {}
    return sourceMediums;
  };

  const _updateSourceMediums = (souceMediums) => {
    try {
      let updatedParams = '';
      if (!souceMediums) {
        updatedParams = JSON.stringify(utmParams);
      } else {
        updatedParams = JSON.stringify(Object.assign(souceMediums, utmParams));
      }
      localStorage.setItem('source_medium', updatedParams);
    } catch (e) {}
  };

  return {
    // To fetch source_medium in form handlers
    fetch () {
      const sourceMediums = _getStoredSourceMediums();
      if (sourceMediums) {
        return Object.keys(sourceMediums).map(item => {
          return item + ' | ' + sourceMediums[item];
        }).join(',');
      }
    },
    // To capture and store source_medium values in local storage. Invoked in form's widget.html
    save () {
      const sourceMediums = _getStoredSourceMediums();
      _updateSourceMediums(sourceMediums);
    },
    // To clear source_medium values from local storage after successful signup
    destroy () {
      localStorage.removeItem('source_medium');
    }
  };
};

const connectAnotherWindow = (iframe, payload, iterateAll = false) => {
  if (!payload) return;
  try {
    if (iframe) {
      iframe.contentWindow.postMessage(payload, '*');
    } else if (iterateAll) {
      const urls = window.FW.crossDomainConnect.urls;
      Object.keys(urls).forEach((key) => {
        urls[key].contentWindow.postMessage(payload, '*');
      });
    }
  } catch (e) {
  }
};

const setUtmMedium = () => {
  try {
    const utmMedium = getParameterByName('utm_medium') || getParameterByName('medium');
    if (utmMedium) sessionStorage.setItem('utm_medium', utmMedium);
  } catch (e) {}
};

const getUtmMedium = () => {
  try {
    const utmMedium = sessionStorage.getItem('utm_medium');
    if (utmMedium) return utmMedium;
  } catch (e) {}
};

const getObjectValues = (obj) => {
  if (typeof obj !== 'object') return [];
  if (typeof Object.values === 'function') return Object.values(obj);
  const values = [];
  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) return;
    values.push(obj[key]);
  }
  return values;
};

const getFormattedGAClientID = () => {
  let gaClientId = getGAClientID();
  gaClientId = gaClientId ? gaClientId.match(/\d+\.\d+$/) : '';
  gaClientId = (gaClientId && gaClientId.length > 0) ? gaClientId[0] : '';
  return gaClientId;
};

const setDomainLink = (response) => {
  const redirectUrl = response['product_signup_response']['redirect_url'].replace(/\/$/, '');
  const redirectUrlObj = new window.URL(redirectUrl);
  const domainLink = redirectUrlObj.hostname;
  const gsdata = JSON.parse(localStorage.getItem('gsdata'));
  gsdata.domain_link = domainLink;
  localStorage.setItem('gsdata', JSON.stringify(gsdata));
};

const setFserviceLastAccessedAccountCookie = (url) => {
  try {
    const KEY = 'last_accessed_fs_accounts';
    let accounts = Cookie.get(KEY) || null;
    accounts = (accounts && JSON.parse(atob(accounts))) || [];
    accounts = accounts.length >= 5 ? accounts.slice(0, 4) : accounts;
    accounts.unshift(new URL(url).host);
    Cookie.set(KEY, btoa(JSON.stringify(accounts)), { expires: 30, secure: true });
  } catch (e) {}
};

const pollUntilTruthy = (fn, intervalMs, maxAttempts) => {
  let attempts = 0;

  return new Promise((resolve) => {
    const checkValue = () => {
      const result = fn();
      if (result) {
        resolve(result);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkValue, intervalMs);
      } else {
        resolve('');
      }
    };

    checkValue();
  });
};

const getLocale = (locale) => {
  return {
    de: 'de-DE',
    fr: 'fr-FR',
    es: 'es-ES',
    pt: 'pt-PT',
    'latam-es': 'es-MX',
    nl: 'nl-NL',
    'sv-SE': 'sv-SE',
    en: 'en-US',
    ja: 'ja-JP',
    ru: 'ru-RU',
    ko: 'ko-KR',
    it: 'it-IT',
    'zh-CN': 'zh-CN',
    th: 'th-TH',
    vi: 'vi-VN',
    pl: 'pl-PL',
    'zh-HK': 'zh-TW',
    fi: 'fi-FI',
    tr: 'tr-TR'
  }[locale];
};

const getSignupEndpointLang = (workflowLocale) => {
  return {
    de: 'de',
    fr: 'fr',
    es: 'es',
    pt: 'pt-BR',
    'latam-es': 'es-LA',
    nl: 'nl',
    'sv-SE': 'sv-SE',
    en: 'en',
    ja: 'ja-JP',
    ru: 'ru-RU',
    ko: 'ko',
    it: 'it',
    'zh-CN': 'zh-CN',
    th: 'th',
    vi: 'vi',
    pl: 'pl',
    'zh-HK': 'zh-TW',
    fi: 'fi',
    tr: 'tr'
  }[workflowLocale];
};

const signupToProductRedirect = (url, isExternalIframe) => {
  if (!url) return;
  if (isExternalIframe) {
    window.parent.postMessage(JSON.stringify({ url, signup_finished: true }), '*');
  } else {
    redirectToURL(url);
  }
};

const historyPushState = (url, isAsync) => {
  if (!url) return;
  if (isAsync) {
    setTimeout(() => {
      history.pushState({}, '', url);
    }, 0);
  } else {
    history.pushState({}, '', url);
  }
};

const trimAndremoveTrailingSlash = (str) => (str || '').trim().replace(/\/+$/, '');

const loadScriptWithPromise = (src) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const makePreflightRequest = (url) => {
  return jQuery.ajax({ method: 'OPTIONS', url, contentType: 'application/json', dataType: 'text' });
};

const isBlockedEmailDomain = (email = '', element) => {
  const form = $(element).closest('form');
  // Skip blockeed domain check for Shopify signup and Google SSO
  if (isShopifySignup() || isGoogleSSO(form)) return false;

  const productName = document.body.getAttribute('data-product-name');
  const emailDomain = (email.split('@')[1] || '').toLowerCase();
  if (!emailDomain) return false;
  const { all_products_signup: blockedDomains = [] } = RestrictedDomains.restrictedDomains;
  if (isBlockedDomain(emailDomain, blockedDomains) && productName !== 'fservice') {
    return true;
  }
  return false;
};

const canIUseSendBeacon = () => {
  return navigator.sendBeacon;
};

const addNoOpener = () => {
  const blankTargetElements = document.querySelectorAll('[target="_blank"]');
  blankTargetElements.forEach(element => {
    const relAttributeValue = element.getAttribute('rel');
    if (relAttributeValue && relAttributeValue.includes('noopener')) {
      return;
    }
    const newRelAttributeValue = relAttributeValue ? `${relAttributeValue} noopener` : 'noopener';
    element.setAttribute('rel', newRelAttributeValue);
  });
};

const removeParamsURL = (url) => {
  try {
    const urlObj = new URL(url);
    const baseUrl = urlObj.origin + urlObj.pathname;
    return baseUrl;
  } catch (error) {
    return '';
  }
};

const generateUEObj = () => {
  const parseMappings = {
    fw_ue_ppj: 'page_path_journey',
    fw_ue_fr: 'first_referrer',
    fw_ue_pmed: 'pricing_btn_clicked',
    fw_ue_cta: 'ctas_clicked',
    fw_ue_ncta: 'non_ctas_clicked',
    fw_ue_navc: 'navigation_click'
  };

  const directMappings = {
    fw_ue_lp: 'landing_page',
    fw_ue_utmkey: 'keyword',
    fw_ue_utmcmp: 'campaign',
    fw_ue_utmsrc: 'source',
    fw_ue_utmmed: 'medium',
    fw_ue_aud: 'paid_search_audience_id'
  };

  const parsedSessionEnrichment = Object.entries(parseMappings).reduce((acc, [cookieKey, propertyName]) => {
    const cookieValue = Cookie.get(cookieKey);
    if (cookieValue) {
      try {
        acc[propertyName] = JSON.parse(cookieValue);
      } catch (e) {
      }
    }
    return acc;
  }, {});

  const directSessionEnrichment = Object.entries(directMappings).reduce((acc, [cookieKey, propertyName]) => {
    const cookieValue = Cookie.get(cookieKey);
    if (cookieValue) {
      acc[propertyName] = cookieValue;
    }
    return acc;
  }, {});

  const additionalProperties = {
    user_id: GA4Utilities.getGAId(),
    signup_referrer: removeParamsURL(window.location.href)
  };
  const existingAccounts = createExistingAccArr();
  if (existingAccounts && existingAccounts.length > 0) {
    additionalProperties.existing_accounts = existingAccounts;
  }
  const sessionEnrichment = Object.assign({}, parsedSessionEnrichment, directSessionEnrichment, additionalProperties);
  return sessionEnrichment;
};

const setCookiesAsArrOfObj = (cookieKey, obj) => {
  const pageUrl = removeParamsURL(window.location.href);
  switch (cookieKey) {
    case 'fw_ue_cta':
      const ctaObj = {
        cta_click: obj.button_text,
        cta_page: pageUrl
      };
      if (obj.destination_url) {
        ctaObj.destination_url = obj.destination_url;
      }
      addObjToCookieArr(cookieKey, ctaObj, 30);
      break;
    case 'fw_ue_ncta':
      const nonCtaObj = {
        source_click: obj.button_text,
        source_page: pageUrl
      };
      if (obj.destination_url) {
        nonCtaObj.destination_url = obj.destination_url;
      }
      addObjToCookieArr(cookieKey, nonCtaObj, 30);
      break;
    case 'fw_ue_navc':
      const navMenuObj = {
        source_click: obj.navigation_labels,
        source_page: pageUrl
      };
      if (obj.destination_url) {
        navMenuObj.destination_url = obj.destination_url;
      }
      addObjToCookieArr(cookieKey, navMenuObj, 30);
      break;
    default:
      break;
  }
};

const addObjToCookieArr = (cookieKey, obj, expiry) => {
  try {
    let cookieArr = getParsedCookie(cookieKey);
    if (!Array.isArray(cookieArr)) {
      cookieArr = [];
    }
    cookieArr.push(obj);
    cookieArr = cookieArr.slice(-10);
    Cookie.set(cookieKey, JSON.stringify(cookieArr), { expires: expiry });
  } catch (error) {
  }
};

const createExistingAccArr = () => {
  let lastAccessedAccounts = Cookie.get('last_accessed_accounts') || null;
  let lastAccessedFsAccounts = Cookie.get('last_accessed_fs_accounts') || null;
  if (!lastAccessedAccounts && !lastAccessedFsAccounts) return;
  const prodKeyArr = [
    'freshdesk',
    'crm_chat',
    'crm_sales',
    'crm_marketer',
    'fw_crm',
    'crm_caller',
    'cxsuite'
  ];
  let existingAccountsArray = [];
  if (lastAccessedAccounts) {
    try {
      lastAccessedAccounts = JSON.parse(atob(lastAccessedAccounts));
      existingAccountsArray = Object.keys(lastAccessedAccounts).reduce((accumulator, prodKey) => {
        if (prodKeyArr.includes(prodKey) && lastAccessedAccounts[prodKey]) {
          accumulator.push({
            product: prodKey,
            account: lastAccessedAccounts[prodKey].slice(0, 2).reverse()
          });
        }
        return accumulator;
      }, []);
    } catch (e) {
    }
  }
  if (lastAccessedFsAccounts) {
    try {
      lastAccessedFsAccounts = JSON.parse(atob(lastAccessedFsAccounts));
      existingAccountsArray.push({
        product: 'freshservice',
        account: lastAccessedFsAccounts.slice(0, 2).reverse()
      });
    } catch (e) {
    }
  }
  return existingAccountsArray;
};

const getParsedCookie = (cookie) => {
  try {
    const cookieValue = Cookie(cookie);
    return cookieValue ? JSON.parse(cookieValue) : [];
  } catch (error) {
    return null;
  }
};

export const getMiscPlanId = (product, currency, pricingPlanType) => {
  let planId = '';
  if (pricingPlanType) {
    switch (product) {
      case 'fwcssuite': {
        const fwcssuitePlanId = {
          select_growth: `crm_cxsuite_growth_${currency}_Yearly`,
          select_pro: `crm_cxsuite_pro_${currency}_Yearly`,
          select_ent: `crm_cxsuite_enterprise_${currency}_Yearly`
        };
        planId = fwcssuitePlanId[pricingPlanType];
      }
        break;
      case 'fdesk': {
        const fdeskPlanId = {
          fdsdfree: 'free_21',
          fdsdgrowth: 'growth_21',
          fdsdpro: 'pro_21',
          fdsdenterprise: 'enterprise_21'
        };
        planId = fdeskPlanId[pricingPlanType];
      }
        break;
      case 'fchat': {
        const fchatPlanId = {
          fdmfree: `crm_messaging_enterprise_${currency}_Yearly`,
          fdmgrowth: `crm_messaging_growth_${currency}_Yearly`,
          fdmpro: `crm_messaging_pro_${currency}_Yearly`,
          fdmenterprise: `crm_messaging_enterprise_${currency}_Yearly`
        };
        planId = fchatPlanId[pricingPlanType];
      }
        break;
      case 'fcaller': {
        const fcallerPlanId = {
          fdccfree: `crm_contact_center_enterprise_${currency}_Yearly`,
          fdccgrowth: `crm_contact_center_growth_${currency}_Yearly`,
          fdccpro: `crm_contact_center_pro_${currency}_Yearly`,
          fdccenterprise: `crm_contact_center_enterprise_${currency}_Yearly`
        };
        planId = fcallerPlanId[pricingPlanType];
      }
        break;
    }

    return planId;
  }
};

export const storeSignupInitialLog = () => {
  const signupLandingTime = sessionStorage.getItem('signupLandingTime');
  const signupInitiateTime = sessionStorage.getItem('signupInitiateTime');
  const isPrefilledForm = sessionStorage.getItem('isPrefilledForm');
  if (signupLandingTime) localStorage.setItem('signupLandingTime', signupLandingTime);
  if (signupInitiateTime) localStorage.setItem('signupInitiateTime', signupInitiateTime);
  if (isPrefilledForm) localStorage.setItem('isPrefilledForm', isPrefilledForm);
};

export const resetInitialSignupPerformanceLog = () => {
  ['signupLandingTime', 'signupInitiateTime', 'initial_form_details', 'isPrefilledForm'].forEach((item) => {
    sessionStorage.removeItem(item);
  });
};

const captureFormInitiateTime = () => {
  !sessionStorage.getItem('signupInitiateTime') && sessionStorage.setItem('signupInitiateTime', new Date().toISOString());
  sessionStorage.setItem('isPrefilledForm', 'false');
};

export const setInitialSignupPerformanceLog = () => {
  resetInitialSignupPerformanceLog();
  sessionStorage.setItem('signupLandingTime', new Date().toISOString());
  const localFormData = localStorage.getItem('form_details');
  if (localFormData) {
    sessionStorage.setItem('initial_form_details', localFormData);
  }
};

const setInitialTargetingCookies = () => {
  const admitadUid = getParameterByName('admitad_uid');
  const allowTargetingCookies = !!(window.OptanonActiveGroups && window.OptanonActiveGroups.includes('C0004'));
  const admitadCookie = Cookie.get('admitad_uid') || '';
  if (allowTargetingCookies) {
    admitadUid && Cookie.set('admitad_uid', admitadUid, { expires: 90 });
  } else if (!allowTargetingCookies && admitadCookie) {
    Cookie.remove('admitad_uid');
  }
};

const buildAdmitadPayload = (orderId) => {
  const admitadPayload = {
    uid: Cookie.get('admitad_uid') || '',
    order_id: orderId
  };
  return admitadPayload.uid ? admitadPayload : null;
};

export default {
  loadScript,
  loadMaxmindScript,
  onModuleLoad,
  classifyStatusCode,
  getParameterByName,
  getUserAgent,
  getUserAgentForMobile,
  redirectToURL,
  convertTimeHours,
  generateUID,
  removeTrailingSlashEnd,
  log,
  debounce,
  getShaHash,
  freshmarketerPost,
  postToKinesis,
  buildFreshmarketerObject,
  configureAssetId,
  fwcrmAssetId,
  sanitizeGACookie,
  customFreshsalesScript,
  convertToHTMLAttribute,
  getCountryCode,
  getCurrentLocation,
  getCurrentReferrer,
  setBackendErrorMessage,
  setTacticID,
  customHiddenFormFields,
  escapeHtml,
  signupErrorLog,
  signupLog,
  signupPerformanceLog,
  resetSignupPerformanceLog,
  triggersignupPerformanceLogApi,
  getLocaleCountryCode,
  selectCountry,
  getTacticID,
  getCompositeKey,
  freshsalesPost,
  sendTransactionalEmail,
  getAppLink,
  getGAClientID,
  addToPerformance,
  getCombinedGaClientIdWithoutGclid,
  setGclId,
  getGclId,
  passSignupResponseToGA,
  getCombinedGaClientId,
  GDPRFunctionalCookieDisabled,
  keyTriggered,
  returnResolvedPromise,
  getLinkedAccounts,
  getProductIconInfo,
  isBusinessEmail,
  isTextHTML,
  showHideFields,
  additionalGenericFields,
  channelInfo,
  connectAnotherWindow,
  freshsalesPostV2,
  restructureFmObj,
  setUtmMedium,
  getUtmMedium,
  getObjectValues,
  getFormattedGAClientID,
  setDomainLink,
  setFserviceLastAccessedAccountCookie,
  pollUntilTruthy,
  getLocale,
  getSignupEndpointLang,
  signupToProductRedirect,
  buildGrowSumoPayload,
  historyPushState,
  trimAndremoveTrailingSlash,
  loadScriptWithPromise,
  getTrackerClientId,
  makePreflightRequest,
  isBlockedEmailDomain,
  canIUseSendBeacon,
  addNoOpener,
  removeParamsURL,
  generateUEObj,
  setCookiesAsArrOfObj,
  addObjToCookieArr,
  createExistingAccArr,
  getMiscPlanId,
  resetInitialSignupPerformanceLog,
  storeSignupInitialLog,
  captureFormInitiateTime,
  setInitialSignupPerformanceLog,
  isShopifySignup,
  isGoogleSSO,
  setInitialTargetingCookies,
  buildAdmitadPayload
};
